/**
 * Stable identifiers for the sample note experience.
 *
 * The sample note is the real product UI (`NoteChatView` and everything it
 * renders) pointed at committed fixture data on a public route. These ids tie
 * the route, the fixtures, and the MSW handlers together.
 *
 * Anti-rot rules (enforced in review, see e2e/features/sample-note):
 * - No sample-note conditionals inside product components. The route, the
 *   auth stub, and the fixtures are the only control surfaces.
 * - Fixtures are typed against `@store/generated/*` so contract drift fails
 *   `pnpm typecheck`.
 */
// Canonical route identifiers live in @shared so the Chrome extension links
// to the same path without a keep-in-sync comment. Re-exported here so
// web-app call sites keep importing from the feature.
export {
  SAMPLE_NOTE_ID,
  SAMPLE_NOTE_PATH,
  SAMPLE_ROUTE_BASE,
} from '@shared/constants/sampleNote';
import { SAMPLE_ROUTE_BASE } from '@shared/constants/sampleNote';
export const SAMPLE_ENCOUNTER_ID = '0b2d4f68-1a3c-45e7-9b8d-2c4e6f8a0b13';
export const SAMPLE_PATIENT_ID = 'c4d5e6f7-a8b9-4c01-9d2e-3f4a5b6c7d80';
export const SAMPLE_TEMPLATE_ID = 'b7f1a0e2-4c3d-4a9b-8e21-5d6f7a8b9c01';
export const SAMPLE_ORGANIZATION_ID = '3f2a1b6c-9d8e-4f70-a1b2-c3d4e5f60789';
export const SAMPLE_PROVIDER_USER_ID = 'sample|provider-anita-patel';

/**
 * Browser-visible app root. The router mounts under VITE_ROUTER_BASENAME
 * (e.g. '/plots/<id>' in garden plots, '' in production), and
 * window.location.pathname includes it — route paths like SAMPLE_ROUTE_BASE
 * do not.
 */
const routerBase = (import.meta.env.VITE_ROUTER_BASENAME || '/').replace(/\/$/, '');

/** Absolute pathname prefix of the sample-note route, basename included. */
export const SAMPLE_ROUTE_LOCATION_PREFIX = `${routerBase}${SAMPLE_ROUTE_BASE}`;

/** Service-worker scope / script URL, both under the app root so the scope
 * restriction (scope must sit inside the worker script's directory) holds in
 * every deployment shape. */
export const SAMPLE_WORKER_SCOPE = SAMPLE_ROUTE_LOCATION_PREFIX;
export const SAMPLE_WORKER_URL = `${routerBase}/mockServiceWorker.js`;

/** True when the current document lives under the sample-note route. */
export const isOnSampleNoteRoute = (): boolean =>
  window.location.pathname.startsWith(SAMPLE_ROUTE_LOCATION_PREFIX);
